import { Box, Button, Collapse, styled } from '@mui/material'
import { forEach } from 'lodash'
import { FC, ReactNode, useEffect, useRef, useState } from 'react'
import ArrowUpIcon from '../../assets/icons/ArrowUp.svg?react'

const CollapseWrapper = styled(Box)<{ showFog: boolean }>(({ theme, showFog }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    gap: '8px',
    position: 'relative',
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '50px',
        left: 0,
        right: 0,
        height: '60px',
        background: showFog
            ? 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%)'
            : 'none',
        pointerEvents: 'none',
    },
}))

interface Props {
    text: ReactNode
    maxLines?: number
    lineHeight?: number
}

export const TextCollapse: FC<Props> = ({ text, maxLines = 2, lineHeight = 24 }) => {
    const [expanded, setExpanded] = useState(false)
    const [showExpandButton, setShowExpandButton] = useState(false)
    const textRef = useRef<HTMLDivElement>(null)
    const collapseRef = useRef<HTMLDivElement>(null)

    const handleClickExpanded = () => {
        setExpanded(!expanded)
    }

    const handleResize = (entries: ResizeObserverEntry[]) => {
        forEach(entries, ({ target }) => {
            const { scrollHeight, clientHeight } = target
            const hasOverflow = scrollHeight > clientHeight

            setShowExpandButton(hasOverflow)
        })
    }

    useEffect(() => {
        if (!textRef.current || !collapseRef.current) {
            return
        }

        const observer = new ResizeObserver(handleResize)

        const contentContainer = collapseRef.current.querySelector('.MuiCollapse-wrapperInner')

        if (contentContainer) {
            observer.observe(contentContainer)
        }

        return () => {
            observer.disconnect()
        }
    }, [text, maxLines, lineHeight])

    return (
        <CollapseWrapper showFog={showExpandButton}>
            <Collapse
                ref={collapseRef}
                in={expanded}
                collapsedSize={`${maxLines * lineHeight}px`}
                sx={{
                    width: '100%',
                    '& .MuiCollapse-wrapperInner': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: expanded ? 'unset' : maxLines,
                        WebkitBoxOrient: 'vertical',
                        wordBreak: 'break-word',
                    },
                }}>
                <div ref={textRef}>{text}</div>
            </Collapse>
            {showExpandButton ? (
                <Button
                    size="small"
                    onClick={handleClickExpanded}
                    endIcon={
                        <ArrowUpIcon
                            style={{
                                transform: 'rotate(180deg)',
                            }}
                        />
                    }>
                    Развернуть
                </Button>
            ) : null}

            {expanded ? (
                <Button
                    size="small"
                    onClick={handleClickExpanded}
                    endIcon={
                        <ArrowUpIcon
                            style={{
                                transform: 'rotate(0deg)',
                            }}
                        />
                    }>
                    Свернуть
                </Button>
            ) : null}
        </CollapseWrapper>
    )
}
